import { useEffect, useRef, useState } from 'preact/hooks';
import { BaseDialog, BtnCancel, DialogHeader } from '@components/dialog';
import { BtnPrimary } from '@components/buttons';
import { useRouter } from '@components/router';
import { navigateUnguarded, onBeforeNavigate } from '@components/router/navigate-guards';
import { useIntl } from 'shared/intl/use-intl';
import { showCompleteLessonError } from './complete-error';

/**
 * Stops the first in-app navigation away from the week's last lesson while it
 * is not marked complete, and asks. "Mark complete and continue" marks it and
 * carries on to where the student was going; "Leave without marking" just
 * goes. Closing the dialog stays on the page. Asks at most once per visit.
 *
 * Render it only for a student who can complete the lesson — never for a
 * guide viewing as a student or a preview membership.
 */
export function LastLessonLeaveGuard({
  lessonId,
  isComplete,
  setLessonComplete,
}: {
  lessonId: UUID;
  isComplete: boolean;
  setLessonComplete(lessonId: UUID, complete: boolean): Promise<unknown>;
}) {
  const intl = useIntl();
  const router = useRouter();
  const [pendingUrl, setPendingUrl] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const hasAsked = useRef(false);

  useEffect(() => {
    hasAsked.current = false;
  }, [lessonId]);

  useEffect(() => {
    if (isComplete) {
      return;
    }
    return onBeforeNavigate((url) => {
      // A link back to this same lesson (sidebar, week list) is not leaving.
      const isSamePage = new URL(url, location.href).pathname === location.pathname;
      if (hasAsked.current || isSamePage) {
        return false;
      }
      hasAsked.current = true;
      setPendingUrl(url);
      return true;
    });
  }, [lessonId, isComplete]);

  if (!pendingUrl) {
    return;
  }

  function leave(url: string) {
    setPendingUrl(undefined);
    navigateUnguarded(() => router.goto(url));
  }

  function stay() {
    if (!isSaving) {
      setPendingUrl(undefined);
    }
  }

  async function markAndContinue(url: string) {
    setIsSaving(true);
    try {
      await setLessonComplete(lessonId, true);
      leave(url);
    } catch (err) {
      setPendingUrl(undefined);
      // Not marked, so the next attempt to leave should ask again — once the
      // student has posted the required comment, say.
      hasAsked.current = false;
      showCompleteLessonError(intl, err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <BaseDialog blurBg={false} onClose={stay}>
      <section class="p-8 flex flex-col gap-6">
        <DialogHeader
          title={intl("This lesson isn't marked complete")}
          subtitle={intl(
            "It's the last lesson this week. Lessons that aren't marked complete count as missing.",
          )}
        />
        <footer class="flex flex-wrap gap-4">
          <BtnPrimary
            class="rounded-full px-8"
            isLoading={isSaving}
            onClick={() => markAndContinue(pendingUrl)}
          >
            {intl('Mark complete and continue')}
          </BtnPrimary>
          <BtnCancel isStandalone disabled={isSaving} onClick={() => leave(pendingUrl)}>
            {intl('Leave without marking')}
          </BtnCancel>
        </footer>
      </section>
    </BaseDialog>
  );
}
